import { FetchStrategySymbol, FetchStrategy, EntityType, MetadataStore, Predicate, EntityQuery, EntityState, EntityManager, EntityKey } from "breeze-client";
import { BehaviorSubject } from "rxjs";
import * as _provider from './entitymanagerprovider';
import * as _utils from './utilities';
import * as _config from './config';
import { ToastrService } from "ngx-toastr";

export class Repository<T extends meta.MetaEntity> {
    private _fetchStrategy: FetchStrategySymbol = FetchStrategy.FromLocalCache;

    constructor(private _entityManagerProvider: _provider.EntityManagerProvider, private _entityTypeName: string, private _resourceName: string, private toastr: ToastrService) {
        var entityType: EntityType;
        if (this._entityTypeName) {
            entityType = <EntityType>this.getMetastore().getEntityType(this._entityTypeName);
            entityType.setProperties({ defaultResourceName: this._resourceName });

            this.getMetastore().setEntityTypeForResourceName(this._resourceName, this._entityTypeName);
        }

        this.manager().hasChangesChanged.subscribe(this.updateHasChanges);

        if (!window["$ms"])
            window["$ms"] = <MetadataStore>this.manager().metadataStore;
    }

    withId(key: string) {
        if (!this._entityTypeName)
            throw new Error("Repository must be created with an entity type specified");

        return this.manager().fetchEntityByKey(this._entityTypeName, key, true)
            .then(function (data) {
                if (!data.entity)
                    throw new Error("Entity not found!");
                return <T>data.entity;
            });
    }

    findInCache(predicate: Predicate, sort?: string) {
        var query = EntityQuery
            .from(this._resourceName)
            .where(predicate);

        if (sort)
            query = query.orderBy(sort);

        return this.executeCacheQuery(query);
    }

    allCached(sort?: string) {
        var query = EntityQuery
            .from(this._resourceName);

        if (sort) query = query.orderBy(sort);

        return this.executeCacheQuery(query);
    }

    hasChanges = new BehaviorSubject(false);

    updateHasChanges = (eventArgs?) => {
        if (this._entityTypeName) {
            this.hasChanges.next(this.manager().hasChanges([this._entityTypeName]));
        }
    }

    cancelChanges(entity?: T) {
        var entities: T[];
        var manager = this.manager();

        if (entity)
            entities = [entity];
        else
            entities = this.allCached();

        entities.forEach(function (entity) {
            if (entity.entityAspect.entityState === EntityState.Added) {
                manager.detachEntity(entity);
            } else {
                entity.entityAspect.rejectChanges();
            }
        });

        this.updateHasChanges();
    }

    executeCacheQuery(query: EntityQuery) {
        return <T[]>this._entityManagerProvider.manager()
            .executeQueryLocally(query);
    }

    getMetastore() {
        return this.manager().metadataStore;
    }

    manager(): EntityManager {
        return this._entityManagerProvider.manager();
    }

    createEntity(config?: {}) {
        var e = <T>this.manager().createEntity(this._entityTypeName, config);

        if ((<any>e).createdDate != null) {
            (<any>e).createdDate = new Date().getTime()
        }

        if ((<any>e).createdByUserId != null) {
            (<any>e).createdByUserId = "client";
        }

        return e;
    }

    addEntity(entity: T) {
        var added = this.manager().addEntity(entity);
        this.updateHasChanges();
        return <T>added;
    }

    saveChanges(entities?: Array<T>) {
        var modified: Array<T>;

        if (entities)
            modified = entities;
        else
            modified = this.allCached().filter((entity) => entity.entityAspect.entityState === EntityState.Added || entity.entityAspect.entityState === EntityState.Deleted || entity.entityAspect.entityState === EntityState.Modified);

        modified.forEach((entity) => {
            (<any>entity.entityAspect).fieldsSaving = {};
            (<any>entity.entityAspect).fieldsSavingEntityState = entity.entityAspect.entityState;

            for (var prop in entity.entityAspect.originalValues) {
                (<any>entity.entityAspect).fieldsSaving[prop] = entity.entityAspect.originalValues[prop];
            }
        });

        return this.manager()
            .saveChanges(modified)
            .then((saveResult) => {
                this.updateHasChanges();
                var message = _utils.formatSavedMessage(saveResult);

                this.toastr.success(_config.resx.toasts.savedData/* + (message ? '<br />' + message : '')*/);
            });
    }

    mergeEntityIntoCache(data: { results: MergeableDto[] }, isPartial?: boolean, entityCallback?: (entity: T, dto: MergeableDto) => void) {
        var entityType = <EntityType>this.getMetastore().getEntityType(this._entityTypeName);
        var manager = this.manager();

        return data.results.map((dto) => {
            var id = dto.id;
            var key = new EntityKey(entityType, id);
            var entity = <T>manager.getEntityByKey(key);
            if (!entity) {
                entity = <T>entityType.createEntity();
                entity.id = id;
                manager.attachEntity(entity);
                entity.isPartial = isPartial === undefined ? true : isPartial;
            }

            this.mapToEntity(entity, dto);

            if (entityCallback)
                entityCallback(entity, dto);

            entity.entityAspect.setUnchanged();

            return entity;
        });
    }

    private mapToEntity(entity: T, dto: MergeableDto): meta.MetaEntity {
        for (var prop in dto) {

            if (entity.hasOwnProperty(prop)) {
                var propInfo = entity.entityType.getProperty(prop, false);
                if (!propInfo || (propInfo && !propInfo.isNavigationProperty))
                    entity[prop] = dto[prop];
            }
        }
        return entity;
    }

    mergeEntityIntoCacheAlt(data: { results: MergeableDtoAlt[] }, isPartial?: boolean, entityCallback?: (entity: T, dto: MergeableDtoAlt) => void) {
        var entityType = <EntityType>this.getMetastore().getEntityType(this._entityTypeName);
        var manager = this.manager();

        return data.results.map((dto) => {
            var id = dto.id;
            var key = new EntityKey(entityType, id);
            var entity = <T>manager.getEntityByKey(key);
            if (!entity) {
                entity = <T>entityType.createEntity();
                entity.id = id;
                manager.attachEntity(entity);
                entity.isPartial = isPartial === undefined ? true : isPartial;
            }

            this.mapToEntityAlt(entity, dto);

            if (entityCallback)
                entityCallback(entity, dto);

            entity.entityAspect.setUnchanged();

            return entity;
        });
    }

    private mapToEntityAlt(entity: T, dto: MergeableDtoAlt): meta.MetaEntity {
        for (var prop in dto) {

            if (entity.hasOwnProperty(prop)) {
                var propInfo = entity.entityType.getProperty(prop, false);
                if (!propInfo || (propInfo && !propInfo.isNavigationProperty))
                    entity[prop] = dto[prop];
            }
        }
        return entity;
    }
}

export function create<T extends meta.MetaEntity>(entityManagerProvider: _provider.EntityManagerProvider, entityTypeName: string, resourceName: string, toastr: ToastrService) {
    return new Repository<T>(entityManagerProvider, entityTypeName, resourceName, toastr);
}
